import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { eps } from './url.service'
import { Business } from '../store/business/business.state'
import { CronScheduleType } from '../components/hrmsLayout/cron/cron.component'

export interface CronLog {
  id: string
  cronName: string
  targetDate: string
  businessId: string | null
  status: 'queued' | 'running' | 'success' | 'failed' | 'stopped' | 'paused'
  totalItems: number
  processedItems: number
  progressPercent: number
  remainingItems: number
  currentStep: string | null
  triggeredBy: string
  startedAt: string
  completedAt: string | null
  summary: Record<string, number> | null
  errorMessage: string | null
  username: string | null
  business: Business
  businessName: string | null
}

export interface CronLogsResponse {
  data: CronLog[]
  total: number
  page: number
  limit: number
}

export interface CronLogDetail {
  id: string
  logId: string
  cronName: string
  userId: string
  businessId: string | null
  status: 'failed' | 'edge_case'
  action: string
  message: string
  createdAt: string
}

export interface RunCronPayload {
  date?: string
  startDate?: string
  endDate?: string
  businessId?: string
}

export interface CronSchedulePayload {
  cronName: string
  scheduleType: CronScheduleType

  runTime: string

  runAt?: string

  businessId?: string

  isActive?: boolean

  cronExpression: string

  timezone?: string

  weekDay?: number
  monthDay?: number
  month?: number
  annualDay?: number

  updatedBy: string
}

@Injectable({ providedIn: 'root' })
export class CronService {
  private apiUrl = eps.cron.adminApi
  private cronScheduleApiUrl = eps.cron.cronScheduleApi
  constructor(private http: HttpClient) {}

  getAvailableCrons(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/available`)
  }

  runCron(cronName: string, payload: RunCronPayload): Observable<any> {
    return this.http.post(`${this.apiUrl}/run/${cronName}`, payload)
  }

  getLogs(params: {
    cronName?: string
    status?: string
    businessId?: string
    fromDate?: string
    toDate?: string
    page?: number
    limit?: number
  }): Observable<CronLogsResponse> {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value))
      }
    })
    return this.http.get<CronLogsResponse>(`${this.apiUrl}/logs?${query.toString()}`)
  }

  getLog(id: string): Observable<CronLog> {
    return this.http.get<CronLog>(`${this.apiUrl}/logs/${id}`)
  }

  getLogDetails(
    id: string,
    params: { status?: string; page?: number; limit?: number } = {}
  ): Observable<{ data: CronLogDetail[]; total: number }> {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value))
      }
    })
    return this.http.get<{ data: CronLogDetail[]; total: number }>(
      `${this.apiUrl}/logs/${id}/details?${query.toString()}`
    )
  }

  stopCron(id: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/logs/${id}/stop`, {})
  }

  pauseCron(id: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/logs/${id}/pause`, {})
  }

  restartCron(id: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/logs/${id}/restart`, {})
  }

  createSchedule(payload: CronSchedulePayload) {
    return this.http.post<any>(`${this.cronScheduleApiUrl}`, payload)
  }

  updateSchedule(id: string, payload: CronSchedulePayload) {
    return this.http.put<any>(`${this.cronScheduleApiUrl}/${id}`, payload)
  }

  getCronSchedules(params?: any) {
    return this.http.get<any>(`${this.cronScheduleApiUrl}`, { params })
  }

  toggleSchedule(id: string, enabled: boolean) {
    return this.http.patch<any>(`${this.cronScheduleApiUrl}/${id}/toggle`, {
      enabled
    })
  }

  getScheduleExecutionHistory(
    scheduleId: string,
    params: { page?: number; limit?: number; status?: string } = {}
  ): Observable<any> {
    return this.http.get(`${this.apiUrl}/history/${scheduleId}`, {
      params: params as any
    })
  }
}
